// ===========================================
// Link an enquiry to an existing family (enquiries plan §7, Phase 2, A6)
// ===========================================
// From the panel's existing-family chip. Linking moves the enquiry to Onboarding (Enrolled when
// the school switched Onboarding off); from Spam, Enrolled or Onboarding the link is recorded and
// the stage stays. The dialog says which before it happens.

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { Enquiry } from '../../../api/enquiry.api';
import { useEnquiryConfig, useLinkEnquiryFamily } from '../../../hooks/useEnquiries';
import { stageLabel, stageView } from './enquiryUi';

// A6's exceptions (BE `AUTO_RULES.FAMILY_LINKED`): the link is recorded and the stage stays.
const STAYS: readonly Enquiry['stage'][] = ['SPAM', 'ENROLLED', 'ONBOARDING', 'RESOLVED_TO_REVIEW'];

export interface LinkFamilyDialogProps {
  enquiry: Enquiry;
  family: { familyId: string; name: string } | null;
  onClose: () => void;
}

export default function LinkFamilyDialog({ enquiry, family, onClose }: LinkFamilyDialogProps) {
  const { data: config } = useEnquiryConfig();
  const link = useLinkEnquiryFamily();
  if (!family) return null;

  const target = stageView(config, 'ONBOARDING')?.enabled === false ? 'ENROLLED' : 'ONBOARDING';
  const stays = STAYS.includes(enquiry.stage);

  return (
    <Dialog open onClose={link.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Link to {family.name}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          This records that the enquiry is the family <strong>{family.name}</strong>.
        </Typography>
        <Alert severity="info" sx={{ mt: 1 }}>
          {stays
            ? `The enquiry stays at ${stageLabel(config, enquiry.stage)}.`
            : `The enquiry moves to ${stageLabel(config, target)}.`}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={link.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={link.isPending}
          onClick={() => link.mutate({ id: enquiry.id, data: { familyId: family.familyId } }, { onSuccess: () => onClose() })}
        >
          {link.isPending ? 'Linking...' : 'Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
